/**
 * 购物车的纯计算逻辑。
 *
 * 单独抽出来是为了能被直接测试 —— 这些规则错了就是「用户付了钱东西还在车里被扣掉」
 * 或者「点一下数量翻倍」，之前把它们埋在 CartContext 里，测试只能在浏览器里
 * 把逻辑重写一遍来断言，等于什么都没测。
 */

/**
 * 购物车行的唯一标识。
 *
 * 同一件商品的不同 size/sku 是不同的行，但共享同一个数据库 _id，
 * 所以不能拿 _id 当行标识。
 *
 * 只要 size 或 sku 有一个就要纳入 key：schema 允许商品只有 sizes 没有 skus，
 * 若只在「两个都有」时才组合，不同尺码会塌陷成同一行。
 */
export function cartLineKey(productId, size, sku) {
  return size || sku ? `${productId}-${size || ''}-${sku || ''}` : productId
}

/**
 * 从 localStorage 读出来的行做规整：重算 key + 合并同 key 的行。
 *
 * 历史上出现过两种脏数据，都还留在老用户的浏览器里：
 *  1. 旧 addToCart 查找时比较裸 productId、写入时用组合 key，
 *     导致同一规格被塞进多条 id 相同的行；
 *  2. 旧 key 规则只在 size 和 sku 都有时才组合，所以「只有 size」的商品
 *     存的是裸 _id，和现在的 `_id-M-` 对不上。
 *
 * 这两种都靠这里按 (_id, size, sku) 重算 key 后合并，一次性收敛。
 */
export function mergeCartRows(rows) {
  if (!Array.isArray(rows)) return []

  const merged = []
  const byKey = new Map()

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue

    const productId = row._id || row.id
    if (!productId) continue

    const size = row.size || null
    const sku = row.sku || null
    const key = cartLineKey(productId, size, sku)
    const quantity = Number(row.quantity) > 0 ? Number(row.quantity) : 1

    const seen = byKey.get(key)
    if (seen) {
      seen.quantity += quantity
    } else {
      const line = { ...row, id: key, _id: productId, size, sku, quantity }
      byKey.set(key, line)
      merged.push(line)
    }
  }

  return merged
}

/**
 * 把一件商品加进购物车（或累加到已有的那一行）。
 */
export function addLine(rows, line, quantity = 1) {
  const key = cartLineKey(line._id, line.size, line.sku)

  if (rows.some(item => item.id === key)) {
    return rows.map(item =>
      item.id === key ? { ...item, quantity: item.quantity + quantity } : item
    )
  }

  return [...rows, { ...line, id: key, quantity }]
}

/**
 * 下单成功后，把买掉的那些行从购物车移除。
 *
 * orderedLines 里必须是购物车行的 id，不是商品 _id —— 同一商品的不同 size/sku
 * 共享 _id，按 _id 匹配会误伤：买了 M×1 却把 L 那行也扣掉 1 件，
 * 用户付过钱的商品就这么从购物车里悄悄少了。
 */
export function removeOrderedLines(rows, orderedLines) {
  if (!Array.isArray(orderedLines) || orderedLines.length === 0) return rows

  return rows
    .map(item => {
      const line = orderedLines.find(l => l.id === item.id)
      if (!line) return item
      return line.quantity >= item.quantity
        ? null
        : { ...item, quantity: item.quantity - line.quantity }
    })
    .filter(Boolean)
}
